import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { fitImageStyle } from './useFullscreenStage';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { isConvexQuad, warpQuadToRect } from '../../image/shared/perspectiveHomography';

const HANDLE = 8;
const STROKE = 2;
const FILL = 'rgba(128, 104, 251, 0.12)';

/** @typedef {[number, number]} Pt */

function defaultCorners(nw, nh) {
    const mx = nw * 0.07;
    const my = nh * 0.07;
    return [
        [mx, my],
        [nw - mx, my],
        [nw - mx, nh - my],
        [mx, nh - my],
    ];
}

const PassportQuadCropPanel = forwardRef(function PassportQuadCropPanel({ imageSrc, onLoaded, fitBox }, ref) {
    const { t } = useTranslation();
    const imgRef = useRef(null);
    const [nw, setNw] = useState(0);
    const [nh, setNh] = useState(0);
    const [box, setBox] = useState({ w: 0, h: 0 });

    const cornersRef = useRef([[0, 0], [100, 0], [100, 100], [0, 100]]);
    const [, bump] = useState(0);
    const force = () => bump((x) => x + 1);

    const dragRef = useRef(null);
    const layoutRef = useRef({ scale: 1 });

    const measure = useCallback(() => {
        const img = imgRef.current;
        if (!img?.naturalWidth) return;
        const rect = img.getBoundingClientRect();
        const scale = rect.width / img.naturalWidth;
        layoutRef.current = { scale };
        setBox({ w: rect.width, h: rect.height });
    }, []);

    const natToDisp = useCallback(([nx, ny]) => {
        const { scale } = layoutRef.current;
        return [nx * scale, ny * scale];
    }, []);

    const dispToNat = useCallback(([dx, dy]) => {
        const { scale } = layoutRef.current;
        return [
            Math.max(0, Math.min(nw || 1, dx / scale)),
            Math.max(0, Math.min(nh || 1, dy / scale)),
        ];
    }, [nw, nh]);

    const onImgLoad = useCallback(() => {
        const img = imgRef.current;
        if (!img) return;
        setNw(img.naturalWidth);
        setNh(img.naturalHeight);
        cornersRef.current = defaultCorners(img.naturalWidth, img.naturalHeight);
        requestAnimationFrame(() => {
            measure();
            force();
            onLoaded?.();
        });
    }, [measure, onLoaded]);

    useEffect(() => {
        const img = imgRef.current;
        if (!img) return;
        const ro = new ResizeObserver(() => {
            measure();
            force();
        });
        ro.observe(img);
        return () => ro.disconnect();
    }, [imageSrc, measure]);

    const dragMatchesPointer = useCallback((e, d) => {
        if (!d) return false;
        if (d.pointerType === 'mouse' && e.pointerType === 'mouse') return true;
        return d.pointerId === e.pointerId;
    }, []);

    useEffect(() => {
        const onMove = (e) => {
            const d = dragRef.current;
            if (!d || !dragMatchesPointer(e, d)) return;
            const img = imgRef.current;
            if (!img) return;
            const r = img.getBoundingClientRect();
            const lx = e.clientX - r.left;
            const ly = e.clientY - r.top;
            if (d.kind === 'corner') {
                cornersRef.current[d.cornerIndex] = dispToNat([lx, ly]);
            } else if (d.kind === 'pan') {
                const curNat = dispToNat([lx, ly]);
                const dx = curNat[0] - d.startNat[0];
                const dy = curNat[1] - d.startNat[1];
                const nw0 = nw || 1;
                const nh0 = nh || 1;
                cornersRef.current = d.startCorners.map(([x, y]) => [
                    Math.max(0, Math.min(nw0 - 1, x + dx)),
                    Math.max(0, Math.min(nh0 - 1, y + dy)),
                ]);
            }
            force();
        };
        const onUp = (e) => {
            const d = dragRef.current;
            if (!d || !dragMatchesPointer(e, d)) return;
            dragRef.current = null;
        };
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('pointercancel', onUp);
        return () => {
            document.removeEventListener('pointermove', onMove);
            document.removeEventListener('pointerup', onUp);
            document.removeEventListener('pointercancel', onUp);
        };
    }, [dispToNat, dragMatchesPointer, nw, nh]);

    const onPointerDownHandle = useCallback((cornerIndex) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragRef.current = {
            kind: 'corner',
            cornerIndex,
            pointerId: e.pointerId,
            pointerType: e.pointerType,
        };
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
    }, []);

    const onPointerDownPan = useCallback(
        (e) => {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            e.preventDefault();
            const img = imgRef.current;
            if (!img || !nw || !nh) return;
            const r = img.getBoundingClientRect();
            const lx = e.clientX - r.left;
            const ly = e.clientY - r.top;
            dragRef.current = {
                kind: 'pan',
                pointerId: e.pointerId,
                pointerType: e.pointerType,
                startNat: dispToNat([lx, ly]),
                startCorners: cornersRef.current.map((p) => [...p]),
            };
            try {
                e.currentTarget.setPointerCapture(e.pointerId);
            } catch {
                /* ignore */
            }
        },
        [dispToNat, nw, nh],
    );

    useImperativeHandle(ref, () => ({
        async getWarpedJpegBlob() {
            const img = imgRef.current;
            if (!img?.naturalWidth) throw new Error('no image');
            const quad = cornersRef.current.map((p) => [...p]);
            if (!isConvexQuad(quad)) throw new Error('invalid quad');

            const topEdge = Math.hypot(quad[1][0] - quad[0][0], quad[1][1] - quad[0][1]);
            const rightEdge = Math.hypot(quad[2][0] - quad[1][0], quad[2][1] - quad[1][1]);
            const bottomEdge = Math.hypot(quad[3][0] - quad[2][0], quad[3][1] - quad[2][1]);
            const leftEdge = Math.hypot(quad[0][0] - quad[3][0], quad[0][1] - quad[3][1]);
            const avgW = (topEdge + bottomEdge) / 2;
            const avgH = (rightEdge + leftEdge) / 2;

            const maxSrc = Math.max(img.naturalWidth, img.naturalHeight);
            const scale = Math.min(2400, maxSrc * 1.5) / Math.max(avgW, avgH);
            const outW = Math.max(200, Math.round(avgW * scale));
            const outH = Math.max(200, Math.round(avgH * scale));

            const warped = warpQuadToRect(img, quad, outW, outH);
            return await new Promise((resolve, reject) => {
                warped.toBlob(
                    (blob) => { if (blob) resolve(blob); else reject(new Error('blob')); },
                    'image/jpeg',
                    0.93,
                );
            });
        },
    }), []);

    const corners = cornersRef.current;
    const [d0, d1, d2, d3] = corners.map(natToDisp);
    const polyPts = `${d0[0]},${d0[1]} ${d1[0]},${d1[1]} ${d2[0]},${d2[1]} ${d3[0]},${d3[1]}`;

    return (
        <div className="relative inline-block max-w-full select-none touch-none">
            <img
                ref={imgRef}
                src={imageSrc}
                alt=""
                draggable={false}
                onLoad={onImgLoad}
                // Fitted to the modal's measured stage, aspect ratio kept — the
                // whole photo in view, nothing to scroll (see useFullscreenStage).
                style={fitImageStyle(fitBox)}
                className="block object-contain"
            />
            {nw > 0 && nh > 0 && box.w > 0 ? (
                <svg
                    width={box.w}
                    height={box.h}
                    className="pointer-events-none absolute left-0 top-0 overflow-visible"
                >
                    <polygon
                        points={polyPts}
                        fill={FILL}
                        stroke="#8068FB"
                        strokeWidth={STROKE}
                        style={{ pointerEvents: 'visiblePainted', cursor: 'move' }}
                        onPointerDown={onPointerDownPan}
                    />
                    {/* Which edge becomes the TOP of the cropped card, drawn on that
                        edge itself: with the card on its side the dots alone do not
                        say. */}
                    <g
                        transform={`translate(${(d0[0] + d1[0]) / 2} ${(d0[1] + d1[1]) / 2}) rotate(${(Math.atan2(d1[1] - d0[1], d1[0] - d0[0]) * 180) / Math.PI})`}
                        style={{ pointerEvents: 'none' }}
                    >
                        <rect x={-22} y={-9} width={44} height={18} rx={9} fill="#0288d1" />
                        <text x={0} y={4} textAnchor="middle" fontSize={11} fontWeight={600} fill="#fff">
                            {t('passportToPdf.crop.topEdge')}
                        </text>
                    </g>
                    {[d0, d1, d2, d3].map((p, i) => (
                        <circle
                            key={i}
                            cx={p[0]}
                            cy={p[1]}
                            r={HANDLE}
                            fill="#fff"
                            stroke="#ff9800"
                            strokeWidth={2}
                            style={{ pointerEvents: 'all', cursor: 'grab' }}
                            onPointerDown={onPointerDownHandle(i)}
                        />
                    ))}
                </svg>
            ) : null}
        </div>
    );
});

export default PassportQuadCropPanel;
